import { TranslatePipe } from '@ngx-translate/core';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { PSSRImportService } from '#services/api/pssr-import.service';
import { IdName } from '#models/id-name';
import { ImportStatusUI } from '#models/enum/import-status-ui';
import { ImportResponse } from '#models/Import-response';
import { ImportInfo } from '#models/pssr-import';
import { Organization } from '#models/organization';
import { BusinessUnit, Area, SubArea } from '#models/index';
import {
    OrganizationService,
    BusinessUnitsService,
    AreaService,
    SubAreaService,
} from '#services/api';
import { ToastrService } from '#services/shared/toastr.service';

@Component({
    selector: 'app-moc-import',
    templateUrl: './moc-import.component.html',
    styleUrls: ['./moc-import.component.scss'],
    providers: [TranslatePipe],
})
export class MocImportComponent {
    @ViewChild('msgFocusId') msgFocusElem: ElementRef;

    ImportStatusEnum = ImportStatusUI;
    pageTitle = 'Import PSSRs';
    applicationList: IdName[] = [];
    selectedApplication: IdName;
    pssrId = '';
    pssrText = '';
    importMOCStarted = false;
    disableAll = true;
    mocidParam: string = null;
    isMocIdReadonly = false;
    importMOCInfo: ImportInfo = {} as ImportInfo;
    newPssr: ImportInfo = {} as ImportInfo;
    selectedBU: BusinessUnit = new BusinessUnit();
    selectedOrg: Organization = new Organization();
    selectedSubArea: SubArea = new SubArea();
    selectedArea: Area = new Area();
    lastCallOrgBUId = 0;
    lastCallAreaBUId = 0;
    lastCallAreaId = 0;
    lstBUs: BusinessUnit[] = [];
    lstOrgs: Organization[];
    lstArea: Area[];
    lstSubArea: SubArea[];

    constructor(
        private toastrService: ToastrService,
        private pssrImportService: PSSRImportService,

        private translatePipe: TranslatePipe,
        private svcBU: BusinessUnitsService,
        private svcOrg: OrganizationService,
        private svcArea: AreaService,
        private svcSubArea: SubAreaService,
        private svcRouter: Router,
        private route: ActivatedRoute
    ) {
        this.route.queryParams.subscribe(params => {
            for (const key in params) {
                if (key.toLowerCase() === 'mocid') {
                    this.mocidParam = params[key];
                    this.pssrId = this.mocidParam;
                    this.isMocIdReadonly = true;
                    this.loadApplications().then(() => {
                        const imoc = this.applicationList.find(app => app.Name && app.Name.toLowerCase() === 'imoc');
                        if (imoc) {
                            this.selectedApplication = imoc;
                        }
                    });
                    break;
                }
            }
        });
    }

    async onChangeDisableAll() {
        this.disableAll =
            !!this.selectedBU?.Id &&
                this.selectedArea?.Id &&
                this.selectedSubArea?.Id &&
                this.selectedApplication?.Id
                ? false
                : true;
    }

    importMocCreatePssr() {
        this.importMOCStarted = true;
        try {
            if (this.importMOCInfo && this.importMOCInfo.id) {
                this.importItem(this.importMOCInfo).then(() => {
                    if (this.importMOCInfo.status === ImportStatusUI.Imported) {
                        this.importMOCStarted = true;
                    } else {
                        this.importMOCStarted = false;
                    }
                });
            } else {
                this.importMOCStarted = false;
            }
        } catch (e) {
            this.setErrMessage('Error happened while importing PSSR');
            this.importMOCStarted = false;
        }
    }

    async importItem(item: ImportInfo) {
        item.status = ImportStatusUI.InProgress;

        try {
            await this.pssrImportService
                .importPSSR(
                    item.id,
                    this.selectedApplication.Id,
                    this.selectedBU.Id,
                    this.selectedOrg.Id,
                    this.selectedArea.Id,
                    this.selectedSubArea.Id
                )
                .toPromise()
                .then((data: ImportResponse) => {
                    if (data) {
                        item.pssrId = data.PSSRId;
                        switch (data.Status) {
                            case 1:
                                item.status = ImportStatusUI.Imported;
                                break;
                            case 0:
                                item.status = ImportStatusUI.Pending;
                                break;
                            case 2:
                                item.status = ImportStatusUI.Warning;
                                item.error = data.Errors?.join('\r\n') || '';
                                break;
                            case 3:
                                item.status = ImportStatusUI.Error;
                                item.error = data.Errors?.join('\r\n') || '';
                                break;
                            default:
                                item.status = ImportStatusUI.Error;
                                item.error = 'Unsupported status';
                        }
                    }
                    this.newPssr = item;
                });
        } catch (e) {
            this.setErrMessage('Error importing item');
        }
    }

    setErrMessage(message: string) {
        this.msgFocusElem.nativeElement.focus();
        this.toastrService.showWarning(this.translatePipe.transform(message));
    }

    async loadApplications() {
        if (!this.applicationList?.length) {
            try {
                const data = await this.pssrImportService.getApplications().toPromise();
                this.applicationList = data.sort((a, b) => a.Name.localeCompare(b.Name));

                if (this.mocidParam) {
                    this.trySelectImocApplication();

                    if (this.selectedApplication?.Id) {
                        this.fetchPssrInfo();
                    }
                }
            } catch (error) {
                this.setErrMessage('Failed to load applications');
                console.error('Error loading applications:', error);
            }
        } else if (this.mocidParam && !this.selectedApplication) {
            this.trySelectImocApplication();
        }

        return this.applicationList;
    }

    private trySelectImocApplication() {
        const imoc = this.applicationList.find(app =>
            app.Name && app.Name.toLowerCase() === 'imoc'
        );

        if (imoc) {
            this.selectedApplication = imoc;
        }
    }

    private fetchPssrInfo() {
        if (!this.selectedApplication?.Id || !this.pssrId) {
            return;
        }

        this.pssrImportService.getPSSRInfo(this.selectedApplication.Id, this.pssrId)
            .subscribe(
                (data: ImportResponse) => {
                    if (data) {
                        this.pssrText = data.Name;
                        this.importMOCInfo = {
                            id: data.Id,
                            name: data.Name,
                            selected: false,
                            status: ImportStatusUI.Pending,
                            error: '',
                            pssrId: null,
                        };
                    }
                },
                (error) => {
                    this.setErrMessage(error.error?.ExceptionMessage || 'Failed to fetch PSSR info');
                }
            );
    }

    getStatusText(pssrStatus: ImportStatusUI): string {
        switch (pssrStatus) {
            case ImportStatusUI.InProgress:
                return 'In Progress';
            case ImportStatusUI.Pending:
                return 'Pending';
            case ImportStatusUI.Imported:
                return 'Imported';
            default:
                return pssrStatus?.toString() || '';
        }
    }

    loadBUs() {
        if (!this.lstBUs?.length) {
            this.svcBU.getUserBUs().toPromise().then((data: BusinessUnit[]) => {
                this.lstBUs = data;
                if (this.lstBUs.length > 0) {
                    this.selectedBU = this.lstBUs[0];
                    this.changeBU();
                }
            });
        }
    }

    async changeBU() {
        this.selectedOrg = null;
        this.selectedArea = null;
        this.selectedSubArea = null;
        this.lstOrgs = null;
        this.lstArea = null;
        this.lstSubArea = null;
        this.lastCallOrgBUId = null;
        this.lastCallAreaBUId = null;
        this.onChangeDisableAll();
    }

    loadOrganizations() {
        if (this.selectedBU?.Id > 0 && this.selectedBU.Id !== this.lastCallOrgBUId) {
            this.lastCallOrgBUId = this.selectedBU.Id;
            this.svcOrg.getListByBU(this.selectedBU.Id).toPromise().then((data: Organization[]) => {
                this.lstOrgs = data.sort((a, b) => a.Name.localeCompare(b.Name));
            });
        }
    }

    changeOrganization() {
        this.selectedArea = null;
        this.selectedSubArea = null;
        this.lstSubArea = null;
        this.onChangeDisableAll();
    }

    loadArea() {
        if (this.selectedBU?.Id > 0 && this.selectedBU.Id !== this.lastCallAreaBUId) {
            this.lastCallAreaBUId = this.selectedBU.Id;
            this.svcArea.getAllByBusinessUnit(this.selectedBU.Id).toPromise().then((data: Area[]) => {
                this.lstArea = data.sort((a, b) => a.Name.localeCompare(b.Name));
            });
        }
    }

    async changeArea() {
        this.selectedSubArea = null;
        this.lstSubArea = null;
        this.onChangeDisableAll();
    }

    loadSubArea() {
        if (this.selectedArea && this.selectedArea.Id > 0 && this.selectedArea.Id !== this.lastCallAreaId) {
            this.lastCallAreaId = this.selectedArea.Id;
            this.svcSubArea.getAllByAreaId(this.selectedArea.Id).toPromise().then((data: SubArea[]) => {
                this.lstSubArea = data.sort((a, b) => a.Name.localeCompare(b.Name));
            });
        }
    }

    editPSSR(pssrId: number): void {
        const url = this.svcRouter.serializeUrl(
            this.svcRouter.createUrlTree(['/pssr-edit/', { id: pssrId }])
        );

        window.open(url, '_blank');
    }

}
